import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, WhatsappLogo } from "@phosphor-icons/react";
import { useLang } from "../../context/LanguageContext";
import { mediaUrl } from "../../lib/api";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

export const Hero = ({ content, onNav }) => {
  const { lang, t, pick } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const hero = content?.hero || {};
  const titleLines = hero[`title_${lang}`] || [];
  const phone = content?.contact?.phone1 || "";
  const wa = content?.contact?.whatsapp || "";

  return (
    <section ref={ref} id="hero" className="relative min-h-[100svh] overflow-hidden bg-ink grid-lines" data-testid="hero-section">
      {/* Background video (with image poster fallback), clipped and parallaxed */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        {content?.video?.url ? (
          <video
            src={mediaUrl(content.video.url)}
            poster={mediaUrl(hero.image)}
            className="w-full h-full object-cover opacity-45"
            autoPlay muted loop playsInline preload="auto"
            data-testid="hero-bg-video"
          />
        ) : (
          <img src={mediaUrl(hero.image)} alt="MY TAXI ride" className="w-full h-full object-cover opacity-45" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 min-h-[100svh] flex flex-col justify-center pt-24 pb-16">
        {/* Dynamic logo */}
        <motion.img
          initial={{ opacity: 0, scale: 0.8, rotate: -6 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          src={mediaUrl(content?.logo)} alt="MY TAXI logo"
          className="h-28 w-28 md:h-40 md:w-40 object-contain mb-8 drop-shadow-[0_0_40px_rgba(255,193,7,0.25)]"
          data-testid="hero-logo"
        />

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="inline-flex items-center gap-3 mb-7 self-start border border-taxi/40 rounded-full px-4 py-1.5">
          <span className="h-2 w-2 rounded-full bg-taxi animate-pulse" />
          <span className="text-taxi text-xs md:text-sm font-bold uppercase tracking-[0.2em]">{pick(hero, "badge")}</span>
        </motion.div>

        <h1 className="font-display text-white text-[15vw] leading-[0.86] md:text-[8.5vw] lg:text-[7.5rem] max-w-5xl" data-testid="hero-title">
          {titleLines.map((ln, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {i === titleLines.length - 1 ? <span className="text-taxi">{ln}</span> : ln}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-8 max-w-xl text-white/70 text-base md:text-lg leading-relaxed">
          {pick(hero, "subtitle")}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4">
          <button onClick={() => onNav("book")} data-testid="hero-book-btn"
            className="group bg-taxi text-black font-bold text-base px-8 py-4 rounded-full hover:bg-white transition-colors flex items-center gap-2">
            {t("hero_book")}
            <ArrowDown size={18} weight="bold" className="group-hover:translate-y-0.5 transition-transform" />
          </button>
          <a href={`tel:${phone.replace(/[^0-9+]/g, "")}`} data-testid="hero-call-btn"
            className="border border-white/25 text-white font-bold text-base px-8 py-4 rounded-full hover:border-taxi hover:text-taxi transition-colors flex items-center gap-2">
            <Phone size={18} weight="fill" /> {t("hero_call")}
          </a>
          <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn"
            className="bg-[#25D366] text-black font-bold text-base px-8 py-4 rounded-full hover:brightness-110 transition-all flex items-center gap-2">
            <WhatsappLogo size={20} weight="fill" /> WhatsApp
          </a>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40">
        <span className="text-[10px] uppercase tracking-[0.3em]">{t("scroll")}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={16} />
        </motion.div>
      </div>
    </section>
  );
};
